import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import pages from "../../../pages/routes";

const MIN_REMOTE_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 180;

const RESULT_TYPE_LABELS = {
  page: "Pages",
  device: "Devices",
  alert: "Active Alerts",
  group: "Groups",
  teamMember: "Team",
  notificationRule: "Notifications",
  role: "Roles",
  logSearch: "Logs",
};

const RESULT_TYPE_ICONS = {
  page: "apps",
  device: "dns",
  alert: "notifications_active",
  group: "group",
  teamMember: "person",
  notificationRule: "rule",
  role: "security",
  logSearch: "history",
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const resultMatchesQuery = (result, query) => {
  if (!query) return true;
  const searchable = [
    result.title,
    result.subtitle,
    result.type,
    result.badge,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return searchable.includes(query);
};

function buildPageResults(user, resources) {
  return pages(user || {}, resources || {})
    .filter((page) => !page.disabled && !page.unlisted)
    .flatMap((page) => {
      const pageResults = [];

      if (page.defaultPath || page.basePath) {
        pageResults.push({
          type: "page",
          id: `page:${page.basePath}`,
          title: page.headerTitle,
          subtitle: "Page",
          route: page.defaultPath || page.basePath,
          icon: page.headerIcon,
        });
      }

      (page.sections || [])
        .filter((section) => !section.disabled)
        .forEach((section) => {
          pageResults.push({
            type: "page",
            id: `section:${section.path}`,
            title: section.label,
            subtitle: page.headerTitle,
            route: section.path,
            icon: section.icon || page.headerIcon,
          });
        });

      return pageResults;
    });
}

function buildLogSearchResult(query, user, resources) {
  if (query.length < MIN_REMOTE_QUERY_LENGTH) {
    return [];
  }

  const logsPage = pages(user || {}, resources || {}).find(
    (page) => page.basePath === "/activity" && !page.disabled,
  );

  if (!logsPage) {
    return [];
  }

  const encodedQuery = encodeURIComponent(query);

  return [
    {
      type: "logSearch",
      id: `log-search:${query}`,
      title: query,
      subtitle: "Event History",
      route: `/activity/notifications?search=${encodedQuery}`,
      icon: "history",
      badge: "Search logs",
    },
  ];
}

function getGroupedResults(results) {
  return results.reduce((groups, result) => {
    const label = RESULT_TYPE_LABELS[result.type] || "Results";
    if (!groups.has(label)) {
      groups.set(label, []);
    }
    groups.get(label).push(result);
    return groups;
  }, new Map());
}

function GlobalSearch({
  isOpen,
  onClose,
  user,
  resources,
  history,
}) {
  const [query, setQuery] = useState("");
  const [remoteResults, setRemoteResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const cancelSourceRef = useRef(null);

  const normalizedQuery = normalizeText(query);
  const pageResults = useMemo(
    () =>
      buildPageResults(user, resources)
        .filter((result) => resultMatchesQuery(result, normalizedQuery))
        .slice(0, normalizedQuery ? 6 : 10),
    [normalizedQuery, resources, user],
  );
  const logSearchResults = useMemo(
    () => buildLogSearchResult(normalizedQuery, user, resources),
    [normalizedQuery, resources, user],
  );
  const results = useMemo(
    () => [...pageResults, ...logSearchResults, ...remoteResults],
    [logSearchResults, pageResults, remoteResults],
  );
  const groupedResults = useMemo(() => getGroupedResults(results), [results]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    setActiveIndex(0);

    if (normalizedQuery.length < MIN_REMOTE_QUERY_LENGTH) {
      cancelSourceRef.current?.cancel("query too short");
      setRemoteResults([]);
      setLoading(false);
      setErrorMessage("");
      return undefined;
    }

    setLoading(true);
    setErrorMessage("");

    const timer = window.setTimeout(() => {
      cancelSourceRef.current?.cancel("superseded search request");
      const source = axios.CancelToken.source();
      cancelSourceRef.current = source;

      axios
        .get("/v2/search", {
          params: {
            q: normalizedQuery,
            limit: 8,
          },
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          cancelToken: source.token,
        })
        .then((res) => {
          setRemoteResults(Array.isArray(res.data?.data) ? res.data.data : []);
          setLoading(false);
        })
        .catch((error) => {
          if (axios.isCancel(error)) {
            return;
          }

          setRemoteResults([]);
          setLoading(false);
          setErrorMessage("Search unavailable");
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [isOpen, normalizedQuery]);

  useEffect(() => {
    if (activeIndex > results.length - 1) {
      setActiveIndex(Math.max(results.length - 1, 0));
    }
  }, [activeIndex, results.length]);

  useEffect(() => {
    if (!isOpen) {
      cancelSourceRef.current?.cancel("search closed");
      setLoading(false);
    }
  }, [isOpen]);

  const navigateToResult = (result) => {
    if (!result?.route) {
      return;
    }

    if (history && typeof history.push === "function") {
      history.push(result.route);
    } else {
      window.location.assign(result.route);
    }
    onClose();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (results.length ? (index + 1) % results.length : 0));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) =>
        results.length ? (index - 1 + results.length) % results.length : 0,
      );
      return;
    }

    if (event.key === "Enter" && results[activeIndex]) {
      event.preventDefault();
      navigateToResult(results[activeIndex]);
    }
  };

  if (!isOpen) {
    return null;
  }

  let renderIndex = -1;

  return (
    <div className="global-search" role="presentation" onKeyDown={handleKeyDown}>
      <button
        type="button"
        className="global-search__backdrop"
        aria-label="Close search"
        onClick={onClose}
      />
      <div
        className="global-search__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Search"
      >
        <div className="global-search__input-row">
          <i className="material-icons" aria-hidden="true">manage_search</i>
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search"
            aria-label="Search"
            autoComplete="off"
            spellCheck="false"
          />
        </div>

        <div className="global-search__result-list" role="listbox" aria-label="Search results">
          {Array.from(groupedResults.entries()).map(([groupLabel, groupResults]) => (
            <div className="global-search__group" key={groupLabel}>
              <div className="global-search__group-label">{groupLabel}</div>
              {groupResults.map((result) => {
                renderIndex += 1;
                const currentIndex = renderIndex;
                const selected = currentIndex === activeIndex;
                const icon = result.icon || RESULT_TYPE_ICONS[result.type] || "search";

                return (
                  <button
                    type="button"
                    key={`${result.type}:${result.id}:${result.route}`}
                    className={`global-search__result${selected ? " active" : ""}`}
                    onMouseEnter={() => setActiveIndex(currentIndex)}
                    onClick={() => navigateToResult(result)}
                    role="option"
                    aria-selected={selected}
                  >
                    <span className={`global-search__icon global-search__icon--${result.type}`}>
                      <i className="material-icons" aria-hidden="true">{icon}</i>
                    </span>
                    <span className="global-search__copy">
                      <span className="global-search__title">{result.title}</span>
                      {result.subtitle ? (
                        <span className="global-search__subtitle">{result.subtitle}</span>
                      ) : null}
                    </span>
                    {result.badge ? (
                      <span className={`global-search__badge global-search__badge--${result.type}`}>
                        {result.badge}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ))}

          {!results.length && !loading ? (
            <div className="global-search__empty">No results</div>
          ) : null}
          {loading ? (
            <div className="global-search__loading">Searching</div>
          ) : null}
          {errorMessage ? (
            <div className="global-search__error">{errorMessage}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default GlobalSearch;
